import {
  OrderItem,
  Product,
  ProductionItem,
  ProductionItemDetail,
  SeedingBatch,
  Tray,
} from 'constants/interface';
import {
  OrderItemApiStatusEnum,
  OrderItemInternalOrderStatus,
  OrderItemInternalProductionItemStatus,
  OrderOtherStatusEnum,
  OrderProposalStatusEnum,
  OrderStatus,
  OrderTypeEnum,
  OrderUnitDisplayMapping,
  OrderUnitEnum,
  ProductionItemStatusEnum,
  SeedingBatchStatusEnum,
  SeedingBatchTypeEnum,
} from 'constants/order';
import { ProductTypeEnum } from 'constants/product';
import { buildTrayDisplay } from './tray';

type OrderTrayQuantity = {
  fullTrayQuantity: number
  remainder: number
}

export const calculateSeedQuantity = (
  quantity: number,
  unit: OrderUnitEnum,
  product: Product,
  tray: Tray,
): number => {
  const baseValue = unit === OrderUnitEnum.TRAY
    ? product.variety.seedConsumption * tray.capacity * quantity
    : quantity * 1000;

  const calculatedTotal = baseValue + (Math
    .round(((product.loss || 0) * baseValue) + Number.EPSILON)
  );

  return calculatedTotal;
};

const OrderUnitMultiplier: Record<
OrderUnitEnum,
(quantity: number, trayCapacity: number) => number
> = {
  [OrderUnitEnum.UNIT]: (quantity) => quantity,
  [OrderUnitEnum.THOUSAND]: (quantity) => (quantity * 1000),
  [OrderUnitEnum.TRAY]: (quantity, trayCapacity) => (quantity * trayCapacity),
};

export const buildOrderSeedQuantityDisplay = (
  quantity: number,
  unit: OrderUnitEnum,
  trayCapacity: number,
): string => {
  const multiplier = OrderUnitMultiplier[unit];
  const formattedQuantity = Intl
    .NumberFormat(
      'pt-BR',
    ).format(multiplier(quantity, trayCapacity));

  return formattedQuantity;
};

export const buildSimpleQuantityDisplay = (
  quantity: number,
): string => {
  // const multiplier = OrderUnitMultiplier[unit];
  const formattedQuantity = Intl
    .NumberFormat(
      'pt-BR',
    ).format(quantity);

  return formattedQuantity;
};

export const getReservedQuantity = (
  productionItem: ProductionItem,
): number => {
  const quantity = productionItem
    .seedReservations
    .reduce((prevReservations, reservation) => (reservation.quantity + prevReservations), 0);

  return quantity;
};

export const getTrayQuantity = (
  totalQuantity: number,
  trayCapacity: number,
  seedConsumption: number,
): OrderTrayQuantity => {
  const divider = (trayCapacity * seedConsumption);

  const decimalQuantity = totalQuantity / divider;

  const fullTrayQuantity = parseInt(`${decimalQuantity}`, 10);
  const remainder = totalQuantity - (fullTrayQuantity * divider);

  return {
    fullTrayQuantity,
    remainder,
  };
};

export const buildTrayQuantityDisplay = (
  orderTrayQuantity: OrderTrayQuantity,
): string => {
  const display = `${
    orderTrayQuantity.fullTrayQuantity} Bdj${orderTrayQuantity.remainder ? ` + ${orderTrayQuantity.remainder} mudas` : ''
  }`;

  return display;
};

export const buildProductionItemQuantityDisplay = (
  item: ProductionItemDetail,
): string => {
  // If item has no reservations, use calculated quantity
  // If it has reservations, use seed reservations reduced quantity

  // const mainSeedingBatch = item.seedingBatches ? item.seedingBatches[0] : undefined;

  // const isReservedQuantity = mainSeedingBatch && mainSeedingBatch.seedReservations.length > 0;
  const isReservedQuantity = item.seedReservations.length > 0;

  const quantity = isReservedQuantity
    ? getReservedQuantity(item)
    : item.calculatedQuantity;

  return `${buildSimpleQuantityDisplay(quantity)} ${isReservedQuantity ? '(Reservado)' : '(Previsto)'}`;
};

const defaultRegularProductDisplayOpts = {
  quantity: true,
  culture: true,
  variety: true,
  tray: true,
};

export const buildRegularProductDisplay = (
  productionItem: ProductionItem,
  opts: {
    quantity?: boolean,
    culture?: boolean,
    variety?: boolean,
    tray?: boolean,
  } = defaultRegularProductDisplayOpts,
): string => {
  const finalOpts = {
    ...defaultRegularProductDisplayOpts,
    ...opts,
  };
  const display = `
  ${finalOpts.quantity ? productionItem.orderItem.orderQuantity : ''}
  ${finalOpts.quantity ? OrderUnitDisplayMapping[productionItem.orderItem.unit].namePlural : ''}
  ${finalOpts.culture ? productionItem.product.variety.culture.name : ''}
  ${finalOpts.variety ? productionItem.product.variety.name : ''}
  ${finalOpts.tray ? buildTrayDisplay(productionItem.tray) : ''}
  `;

  return display;
};

export const buildGraftingProductsDisplay = (
  scion: ProductionItem,
  stock: ProductionItem,
): string => {
  const cultureName = scion.product.variety.culture.name;
  const scionVariety = scion.product.variety.name;
  const scionTrayDisplay = `${scion.tray.capacity}${scion.tray.isHydroponic ? ' Hidro' : ''}`;
  const stockVariety = stock.product.variety.name;
  const stockTrayDisplay = `${stock.tray.capacity}${stock.tray.isHydroponic ? ' Hidro' : ''}`;
  const display = `${
    cultureName
  } ${
    scionVariety
  } ${
    scionTrayDisplay
  } + ${
    stockVariety
  } ${
    stockTrayDisplay
  }`;

  return display;
};

export const buildOrderItemProductDisplay = (
  orderItem: OrderItem,
): string => {
  if (orderItem.order.type === OrderTypeEnum.GRAFTING) {
    const scion = orderItem
      .productionItems
      .find((prodItem) => prodItem.type === ProductTypeEnum.SCION);
    const stock = orderItem
      .productionItems
      .find((prodItem) => prodItem.type === ProductTypeEnum.STOCK);
    if (!scion || !stock) {
      return '';
    }
    return buildGraftingProductsDisplay({
      ...scion,
      orderItem,
    }, {
      ...stock,
      orderItem,
    });
  }

  const standardProductionItem = orderItem
    .productionItems
    .find((prodItem) => prodItem.type === ProductTypeEnum.STANDARD);
  if (!standardProductionItem) {
    return '';
  }
  const display = buildRegularProductDisplay({
    ...standardProductionItem,
    orderItem,
  }, {
    quantity: false,
  });

  return display;
};

const getMainSeedingBatch = (
  item: ProductionItem,
): Omit<SeedingBatch, 'productionItem'> | undefined => {
  const mainSeedingBatch = item.seedingBatches
    ? item.seedingBatches.find((seedingBatch) => seedingBatch.type === SeedingBatchTypeEnum.MAIN)
    : undefined;

  return mainSeedingBatch;
};

export const getSeedingStatus = (
  item: ProductionItem,
): SeedingBatchStatusEnum => {
  const mainSeedingBatch = getMainSeedingBatch(item);
  if (!mainSeedingBatch) {
    return SeedingBatchStatusEnum.PENDING;
  }

  // Check seeding schedule date
  const scheduleStatus = !!mainSeedingBatch.seedingDate || false;

  // Check seed reservation
  const reservationStatus = item.seedReservations.length > 0;

  const status = (scheduleStatus && reservationStatus)
    ? SeedingBatchStatusEnum.CONFIRMED
    : SeedingBatchStatusEnum.PENDING;

  return status;
};

export const getOrderItemApiStatus = (
  orderItem: OrderItem,
): OrderItemApiStatusEnum => {
  const InternalOrderStatusMapping: Record<OrderItemInternalOrderStatus, OrderItemApiStatusEnum> = {
    [OrderProposalStatusEnum.CONFIRMED]: OrderItemApiStatusEnum.CONFIRMED,
    [OrderProposalStatusEnum.PENDING_ADVANCE]: OrderItemApiStatusEnum.PENDING_ADVANCE,
    [OrderProposalStatusEnum.SCHEDULED]: OrderItemApiStatusEnum.SCHEDULED,
    [OrderOtherStatusEnum.CANCELED]: OrderItemApiStatusEnum.CANCELED,
  };
  const InternalProductionItemStatusMapping: Record<
  OrderItemInternalProductionItemStatus,
  OrderItemApiStatusEnum
  > = {
    [ProductionItemStatusEnum.CONFIRMED]: OrderItemApiStatusEnum.CONFIRMED,
    [ProductionItemStatusEnum.SEEDING]: OrderItemApiStatusEnum.SEEDING,
    [ProductionItemStatusEnum.PRODUCTION]: OrderItemApiStatusEnum.PRODUCTION,
    [ProductionItemStatusEnum.DELIVERY]: OrderItemApiStatusEnum.DELIVERY,
    [ProductionItemStatusEnum.FINISHED]: OrderItemApiStatusEnum.FINISHED,
  };

  const isInternalOrderStatus = (
    orderStatus: OrderStatus,
  ): orderStatus is OrderItemInternalOrderStatus => (
    Object.keys(InternalOrderStatusMapping).includes(orderStatus)
  );

  if (isInternalOrderStatus(orderItem.order.status)) {
    return InternalOrderStatusMapping[orderItem.order.status];
  }

  const productionItemStatusPrecedenceList = [
    ProductionItemStatusEnum.CONFIRMED,
    ProductionItemStatusEnum.SEEDING,
    ProductionItemStatusEnum.PRODUCTION,
    ProductionItemStatusEnum.DELIVERY,
    ProductionItemStatusEnum.FINISHED,
  ];

  const lowerStatusFactor = orderItem
    .productionItems
    .reduce((curLowerFactor, prodItem) => {
      const prodItemStatusFactor = productionItemStatusPrecedenceList.indexOf(prodItem.status);
      return prodItemStatusFactor >= 0 && prodItemStatusFactor > curLowerFactor
        ? prodItemStatusFactor
        : curLowerFactor;
    }, 0);

  const lowerFactorProductionItemStatus = productionItemStatusPrecedenceList[lowerStatusFactor]
  || ProductionItemStatusEnum.FINISHED;

  return InternalProductionItemStatusMapping[lowerFactorProductionItemStatus];
};
